import { z } from 'zod';

// ─── Enums ──────────────────────────────────────────────────────────────────

export const MessageGenPhaseSchema = z.enum([
  'idle',
  'gathering_details',
  'awaiting_approval',
  'generating',
  'messages_ready',
  'prioritizing',
  'prioritized',
  'curating',
  'curated',
  'error',
]);

export type MessageGenPhase = z.infer<typeof MessageGenPhaseSchema>;

// ─── Message Sub-schemas ────────────────────────────────────────────────────

export const TrialAnchorSchema = z.object({
  claimId: z.string(),
  studyId: z.string(),
  studyPhase: z.string(),
  claimText: z.string(),
  category: z.string(),
  primaryEndpoint: z.string(),
  targetPatientType: z.string(),
  transplantStatus: z.string().nullable(),
  lineOfTherapy: z.string().nullable(),
});

export type TrialAnchor = z.infer<typeof TrialAnchorSchema>;

export const VoiceAnchorSchema = z.object({
  observationId: z.string(),
  respondentId: z.string(),
  stakeholder: z.string(),
  construct: z.string(),
  quoteText: z.string(),
  quoteQuality: z.string(),
  summary: z.string(),
  themeLabel: z.string(),
  whyQuotable: z.string(),
  respondentSegment: z.string(),
});

export type VoiceAnchor = z.infer<typeof VoiceAnchorSchema>;

export const DoctorNeedAddressedSchema = z.object({
  themeLabel: z.string(),
  domainLabel: z.string(),
  frame: z.string(),
  headline: z.string(),
  nRespondents: z.number().int(),
});

export type DoctorNeedAddressed = z.infer<typeof DoctorNeedAddressedSchema>;

export const CounterpointSchema = z.object({
  observationId: z.string(),
  respondentId: z.string(),
  respondentSegment: z.string(),
  quoteText: z.string(),
  whyThisMatters: z.string(),
});

export type Counterpoint = z.infer<typeof CounterpointSchema>;

export const MessageMetricsSchema = z.object({
  clarity: z.number().min(0).max(1),
  motivating: z.number().min(0).max(1),
  credibility: z.number().min(0).max(1),
  differentiation: z.number().min(0).max(1),
  objectiveResonance: z.number().min(0).max(1),
  brandAlignment: z.number().min(0).max(1),
  audienceFit: z.number().min(0).max(1),
  relevance: z.number().min(0).max(1),
  clarityBasis: z.string().nullable().optional(),
  motivatingBasis: z.string().nullable().optional(),
  credibilityBasis: z.string().nullable().optional(),
  differentiationBasis: z.string().nullable().optional(),
  objectiveResonanceBasis: z.string().nullable().optional(),
  brandAlignmentBasis: z.string().nullable().optional(),
  audienceFitBasis: z.string().nullable().optional(),
  relevanceBasis: z.string().nullable().optional(),
});

export type MessageMetrics = z.infer<typeof MessageMetricsSchema>;
// ─── Brand Names ────────────────────────────────────────────────────────────

export const BRAND_NAMES = ['Elderberry', 'Banana', 'Fig'] as const;

export const BrandNameSchema = z.enum(BRAND_NAMES);

export type BrandName = z.infer<typeof BrandNameSchema>;

// ─── Marketing Message ──────────────────────────────────────────────────────

// These come from external api
export const MarketingMessageSchemaBase = z.object({
  seqIdx: z.number().int().nullable().default(null),
  headline: z.string(),
  body: z.string(),
  category: z.string(),
  frame: z.string().nullable().default(null),
  lens: z.string().nullable().default(null),
  doctorNeedAddressed: DoctorNeedAddressedSchema.nullable().default(null),
  populationSignal: z.string().nullable().default(null),
  relevanceReason: z.string().nullable().default(null),
  trialAnchors: z.array(TrialAnchorSchema).default([]),
  voiceAnchors: z.array(VoiceAnchorSchema).default([]),
  counterpoints: z.array(CounterpointSchema).default([]),
  metrics: MessageMetricsSchema.nullable().default(null),
  rankScore: z.number().nullable().default(null),
  confidence: z.string().nullable().default(null),
  confidenceBasis: z.string().nullable().default(null),
  unsupported: z.boolean().default(false),
});

export const MarketingMessageSchema = MarketingMessageSchemaBase.extend({
  identifier: z.string().default(() => crypto.randomUUID()),
  status: z.enum(['draft', 'approved', 'published']).catch('draft'),
  createdOn: z
    .string()
    .optional()
    .default(() => new Date().toISOString()),
  updatedOn: z
    .string()
    .optional()
    .default(() => new Date().toISOString()),
  saved: z.boolean().default(false),
});

export type MarketingMessage = z.infer<typeof MarketingMessageSchema>;

// ─── Generated Message DTO (from generated_messages table) ─────────────────

export const GeneratedMessageSchema = z.object({
  id: z.number(),
  messageGenerationSessionId: z.number(),
  segmentationSessionId: z.string().nullable(),
  segmentId: z.string().nullable(),
  title: z.string(),
  body: z.string(),
  category: z.string().nullable(),
  brand: z.string().nullable(),
  status: z.string(),
  createdOn: z.string(),
  updatedOn: z.string(),
});

export type GeneratedMessage = z.infer<typeof GeneratedMessageSchema>;

// ─── Chat History Entry ─────────────────────────────────────────────────────

export const MessageGenChatEntrySchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});

export type MessageGenChatEntry = z.infer<typeof MessageGenChatEntrySchema>;

// ─── Canonical State (Single Source of Truth) ───────────────────────────────

const MAX_CHAT_HISTORY = 20;

export const DEFAULT_MAX_MESSAGES = 200;

/**
 * Allowed objectives for message generation.
 * "Generalized" covers non-topic-specific requests.
 * Any objective outside this list is rejected by the gather-details node.
 */
export const ALLOWED_OBJECTIVES = [
  'Tolerability',
  'Safety',
  'Efficacy',
  'Access',
  'Trial Design',
  'Unmet Needs',
  'Generalized',
] as const;

export type AllowedObjective = (typeof ALLOWED_OBJECTIVES)[number];

export const MessageGenStateSchema = z.object({
  phase: MessageGenPhaseSchema.default('idle'),
  name: z.string().nullable().default(null),
  /** Number of messages to filter down to during prioritization. Null = not yet specified. */
  numberOfMessages: z.number().int().positive().nullable().default(null),
  maxMessages: z.number().int().positive().default(DEFAULT_MAX_MESSAGES),
  /** Primary brand for message generation. Defaults to Elderberry. */
  brandName: BrandNameSchema.default('Elderberry'),
  /** One or more segment IDs to target. Empty array means none selected yet. */
  segmentIds: z.array(z.string()).default([]),
  /** Segment IDs locked at session creation (e.g. from the segment dashboard). Immutable after creation. Used by ResetPipeline to restore segmentIds. */
  initialSegmentIds: z.array(z.string()).default([]),
  /** Free-form description of the desired message objective (used for prioritization). */
  objective: z.string().nullable().default(null),
  /** Classified topic bucket — null when objective hasn't been set or couldn't be matched. */
  objectiveCategory: z.enum(ALLOWED_OBJECTIVES).nullable().default(null),
  messages: z.array(MarketingMessageSchema).default([]),
  error: z.string().nullable().default(null),
  chatHistory: z.array(MessageGenChatEntrySchema).default([]),
  /** Text narrative returned by the external agent alongside messages. */
  narrative: z.string().nullable().default(null),
  /** Opaque hash returned by the external agent; passed back on subsequent calls. */
  stateHash: z.string().nullable().default(null),
});

export type MessageGenState = z.infer<typeof MessageGenStateSchema>;

/** Default initial state for a new message generation session. */
export function getDefaultMessageGenState(): MessageGenState {
  return MessageGenStateSchema.parse({});
}

// ─── External Agent Schema (Combined Endpoint) ─────────────────────────────

/**
 * Combined request schema for the single external message agent endpoint.
 * POST /api/segment-messages/[segmentationSessionId]
 *
 * Each pipeline step sends cumulative fields:
 *   - Generation:     session_id, focal_brand, segment_name, wait
 *   - Prioritization: + user_objective, message_count
 *   - Curation:       + user_objective, message_count
 * state_hash is included once available from a prior response.
 */
export const MessageAgentRequestSchema = z.object({
  /** Segmentation session identifier — always required. */
  session_id: z.string(),
  /** Primary brand (defaults to Elderberry). */
  focal_brand: BrandNameSchema.optional(),
  /** Segment display name. */
  segment_name: z.string().optional(),
  /** Free-form user objective. Required for prioritization & curation. */
  user_objective: z.string().optional(),
  /** Number of messages to keep. Required for prioritization & curation. */
  message_count: z.number().int().positive().optional(),
  /** Opaque state hash from a prior response — enables chained calls. */
  state_hash: z.string().optional(),
});

export type MessageAgentRequest = z.infer<typeof MessageAgentRequestSchema>;

export const MessageAgentResponseSchema = z.object({
  messages: z.array(MarketingMessageSchema),
  /** Text narrative summarising the agent's output. */
  narrative: z.string().nullable().default(null),
  /** Opaque hash for chaining subsequent calls. */
  stateHash: z.string().nullable().default(null),
  /** Unique run identifier for this agent invocation. */
  runId: z.string().nullable().default(null),
  /** Agent completion status — expected to be "done". */
  status: z.string().nullable().default(null),
});

export type MessageAgentResponse = z.infer<typeof MessageAgentResponseSchema>;

// ─── Session Schema ─────────────────────────────────────────────────────────

export const MessageGenSessionSchema = z.object({
  id: z.number(),
  sessionId: z.string().nullable(),
  chatInstanceIdentifier: z.string(),
  state: z.record(z.string(), z.unknown()).transform((v) => v as MessageGenState),
  createdOn: z.date(),
  createdBy: z.number(),
});

export type MessageGenSession = z.infer<typeof MessageGenSessionSchema>;

// ─── Session Summary (for listing) ──────────────────────────────────────────

export const MessageGenSessionSummarySchema = z.object({
  id: z.number(),
  sessionId: z.string(),
  name: z.string().nullable(),
  projectTitle: z.string().nullable(),
  phase: MessageGenPhaseSchema,
  createdOn: z.string(),
  segmentIds: z.array(z.string()),
});

export type MessageGenSessionSummary = z.infer<typeof MessageGenSessionSummarySchema>;

// ─── Create Session ─────────────────────────────────────────────────────────

export const CreateMessageGenSessionRequestSchema = z.object({
  sessionId: z.string().min(1).optional(),
  brandIdentifier: z.string().min(1),
  initialState: z
    .object({
      segmentIds: z.array(z.string()).optional(),
    })
    .optional(),
});

export type CreateMessageGenSessionRequest = z.infer<typeof CreateMessageGenSessionRequestSchema>;

// ─── Actions ────────────────────────────────────────────────────────────────

export const MessageGenActionType = {
  SetDetails: 'SET_DETAILS',
  SetObjectiveCategory: 'SET_OBJECTIVE_CATEGORY',
  RequestApproval: 'REQUEST_APPROVAL',
  ApproveGeneration: 'APPROVE_GENERATION',
  SetMessages: 'SET_MESSAGES',
  SetPrioritization: 'SET_PRIORITIZATION',
  SetCuration: 'SET_CURATION',
  UpdateMessage: 'UPDATE_MESSAGE',
  SetError: 'SET_ERROR',
  ClearError: 'CLEAR_ERROR',
  AppendChatHistory: 'APPEND_CHAT_HISTORY',
  SetPhase: 'SET_PHASE',
  ResetPipeline: 'RESET_PIPELINE',
} as const;

export type MessageGenActionType = (typeof MessageGenActionType)[keyof typeof MessageGenActionType];

export type MessageGenAction =
  | {
      type: typeof MessageGenActionType.SetDetails;
      payload: {
        numberOfMessages?: number | null;
        brandName?: string;
        segmentIds?: string[];
        objective?: string | null;
      };
    }
  | {
      type: typeof MessageGenActionType.SetObjectiveCategory;
      payload: {
        objectiveCategory: AllowedObjective | null;
      };
    }
  | { type: typeof MessageGenActionType.RequestApproval }
  | { type: typeof MessageGenActionType.ApproveGeneration }
  | {
      type: typeof MessageGenActionType.SetMessages;
      payload: {
        messages: MarketingMessage[];
        narrative?: string | null;
        stateHash?: string | null;
      };
    }
  | {
      type: typeof MessageGenActionType.SetPrioritization;
      payload: {
        messages: MarketingMessage[];
        narrative?: string | null;
        stateHash?: string | null;
      };
    }
  | {
      type: typeof MessageGenActionType.SetCuration;
      payload: {
        messages: MarketingMessage[];
        narrative?: string | null;
        stateHash?: string | null;
      };
    }
  | {
      type: typeof MessageGenActionType.UpdateMessage;
      payload: {
        index: number;
        updates: Partial<MarketingMessage>;
      };
    }
  | { type: typeof MessageGenActionType.SetError; payload: { error: string } }
  | { type: typeof MessageGenActionType.ClearError }
  | {
      type: typeof MessageGenActionType.AppendChatHistory;
      payload: {
        entries: MessageGenChatEntry[];
      };
    }
  | { type: typeof MessageGenActionType.SetPhase; payload: { phase: MessageGenPhase } }
  | { type: typeof MessageGenActionType.ResetPipeline };

/**
 * Zod schema for actions -- used for the frontend action dispatch endpoint.
 * Each action variant is a separate object with a `type` discriminator.
 */
// @ts-ignore
export const MessageGenActionSchema: z.ZodType<MessageGenAction> = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(MessageGenActionType.SetDetails),
    payload: z.object({
      numberOfMessages: z.number().int().positive().nullable().optional(),
      brandName: z.string().optional(),
      segmentIds: z.array(z.string()).optional(),
      objective: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetObjectiveCategory),
    payload: z.object({
      objectiveCategory: z.enum(ALLOWED_OBJECTIVES).nullable(),
    }),
  }),
  z.object({ type: z.literal(MessageGenActionType.RequestApproval) }),
  z.object({ type: z.literal(MessageGenActionType.ApproveGeneration) }),
  z.object({
    type: z.literal(MessageGenActionType.SetMessages),
    payload: z.object({
      messages: z.array(MarketingMessageSchema),
      narrative: z.string().nullable().optional(),
      stateHash: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetPrioritization),
    payload: z.object({
      messages: z.array(MarketingMessageSchema),
      narrative: z.string().nullable().optional(),
      stateHash: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetCuration),
    payload: z.object({
      messages: z.array(MarketingMessageSchema),
      narrative: z.string().nullable().optional(),
      stateHash: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.UpdateMessage),
    payload: z.object({
      index: z.number().int().min(0),
      updates: MarketingMessageSchema.partial(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetError),
    payload: z.object({ error: z.string() }),
  }),
  z.object({ type: z.literal(MessageGenActionType.ClearError) }),
  z.object({
    type: z.literal(MessageGenActionType.AppendChatHistory),
    payload: z.object({ entries: z.array(MessageGenChatEntrySchema) }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetPhase),
    payload: z.object({ phase: MessageGenPhaseSchema }),
  }),
  z.object({ type: z.literal(MessageGenActionType.ResetPipeline) }),
]);

// ─── Reducer ────────────────────────────────────────────────────────────────

/**
 * Pure reducer for MessageGenState.
 * Single source of truth for all state mutations — used by both
 * backend (session persistence) and frontend (optimistic updates).
 */
export function messageGenReducer(state: MessageGenState, action: MessageGenAction): MessageGenState {
  switch (action.type) {
    case MessageGenActionType.SetDetails: {
      const { numberOfMessages, brandName, segmentIds, objective } = action.payload;
      const next = { ...state };
      if (numberOfMessages !== undefined) {
        next.numberOfMessages =
          numberOfMessages === null ? null : Math.min(numberOfMessages, state.maxMessages);
      }
      if (brandName !== undefined) next.brandName = brandName as BrandName;
      if (segmentIds !== undefined) next.segmentIds = segmentIds;
      if (objective !== undefined) {
        next.objective = objective;
        // Clear category when objective changes — compliance will re-classify
        next.objectiveCategory = null;
      }
      return next;
    }

    case MessageGenActionType.SetObjectiveCategory:
      return { ...state, objectiveCategory: action.payload.objectiveCategory };

    case MessageGenActionType.RequestApproval:
      return { ...state, phase: 'awaiting_approval' };

    case MessageGenActionType.ApproveGeneration:
      return { ...state, phase: 'generating' };

    case MessageGenActionType.SetMessages:
      return {
        ...state,
        phase: 'messages_ready',
        messages: action.payload.messages,
        narrative: action.payload.narrative ?? state.narrative,
        stateHash: action.payload.stateHash ?? state.stateHash,
        error: null,
      };

    case MessageGenActionType.SetPrioritization:
      return {
        ...state,
        phase: 'prioritized',
        messages: action.payload.messages,
        narrative: action.payload.narrative ?? state.narrative,
        stateHash: action.payload.stateHash ?? state.stateHash,
        error: null,
      };

    case MessageGenActionType.SetCuration:
      return {
        ...state,
        phase: 'curated',
        messages: action.payload.messages,
        narrative: action.payload.narrative ?? state.narrative,
        stateHash: action.payload.stateHash ?? state.stateHash,
        error: null,
      };

    case MessageGenActionType.UpdateMessage: {
      const { index, updates } = action.payload;
      if (index < 0 || index >= state.messages.length) return state;
      const updatedMessages = [...state.messages];
      updatedMessages[index] = {
        ...updatedMessages[index],
        ...updates,
        updatedOn: new Date().toISOString(),
      };
      return { ...state, messages: updatedMessages };
    }

    case MessageGenActionType.SetError:
      return { ...state, phase: 'error', error: action.payload.error };

    case MessageGenActionType.ClearError:
      return { ...state, error: null };

    case MessageGenActionType.AppendChatHistory: {
      const merged = [...state.chatHistory, ...action.payload.entries];
      return { ...state, chatHistory: merged.slice(-MAX_CHAT_HISTORY) };
    }

    case MessageGenActionType.SetPhase:
      return { ...state, phase: action.payload.phase };

    case MessageGenActionType.ResetPipeline:
      return {
        ...state,
        phase: 'idle',
        messages: [],
        objective: null,
        numberOfMessages: null,
        objectiveCategory: null,
        error: null,
        narrative: null,
        stateHash: null,
        segmentIds: state.initialSegmentIds,
      };

    default:
      return state;
  }
}

/**
 * Apply a sequence of actions to a state, returning the final state.
 */
export function applyMessageGenActions(state: MessageGenState, actions: MessageGenAction[]): MessageGenState {
  return actions.reduce(messageGenReducer, state);
}

// ─── Chat Message Request ───────────────────────────────────────────────────

export const MessageGenChatRequestSchema = z.object({
  messageGenSessionId: z.number().int().positive(),
  sessionId: z.string().min(1),
  message: z.string().min(1),
});

export type MessageGenChatRequest = z.infer<typeof MessageGenChatRequestSchema>;

// ─── Actions Dispatch Request ───────────────────────────────────────────────

export type MessageGenDispatchActionsRequest = {
  actions: MessageGenAction[];
};

export const MessageGenDispatchActionsRequestSchema: z.ZodType<MessageGenDispatchActionsRequest> = z.object({
  actions: z.array(MessageGenActionSchema).min(1),
});

// ─── Generated Message Create/Update ───────────────────────────────────────

export const CreateGeneratedMessageRequestSchema = z.object({
  messageGenerationSessionId: z.number().int().positive(),
  title: z.string().min(1),
  body: z.string().min(1).max(500),
  category: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  status: z.enum(['draft', 'approved', 'published']).default('draft'),
});

export type CreateGeneratedMessageRequest = z.infer<typeof CreateGeneratedMessageRequestSchema>;

export const UpdateGeneratedMessageRequestSchema = z.object({
  title: z.string().min(1).optional(),
  body: z.string().min(1).max(500).optional(),
  category: z.string().nullable().optional(),
  brand: z.string().nullable().optional(),
  status: z.enum(['draft', 'approved', 'published']).optional(),
});

export type UpdateGeneratedMessageRequest = z.infer<typeof UpdateGeneratedMessageRequestSchema>;
